// Shared mobile-first dark-theme atoms for LIFF pages.
// Values mirror the dashboard tailwind tokens, but tighter padding / font
// sizes for phone viewport (≤414px primary target).

import React from "react";

export function MobileShell({ title, subtitle, children, footer }: {
  title: string; subtitle?: string; children: React.ReactNode; footer?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="min-h-[100dvh] bg-bg-base text-text-primary flex flex-col">
      <header className="sticky top-0 z-10 backdrop-blur bg-bg-base/85 border-b border-border-subtle">
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-text-muted">SENTRY NEXUS</p>
          <h1 className="text-lg font-semibold leading-tight truncate">{title}</h1>
          {subtitle && <p className="text-xs text-text-secondary mt-0.5 truncate">{subtitle}</p>}
        </div>
      </header>
      <main className="flex-1 p-4 pb-24 space-y-4">{children}</main>
      {footer && (
        <footer className="sticky bottom-0 z-10 backdrop-blur bg-bg-base/85 border-t border-border-subtle p-3 space-y-2">
          {footer}
        </footer>
      )}
    </div>
  );
}

export function Card({ children, className = "" }: { children: React.ReactNode; className?: string }): React.ReactElement {
  return <section className={`rounded-2xl bg-bg-surface border border-border p-4 ${className}`}>{children}</section>;
}

export function CardHeader({ title, hint }: { title: string; hint?: string }): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between mb-3">
      <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      {hint && <span className="text-[10px] uppercase tracking-wider text-text-muted">{hint}</span>}
    </div>
  );
}

export function MetricGrid({ items }: { items: { label: string; value: string; tone?: "default" | "success" | "warning" | "danger" }[] }): React.ReactElement {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((m, i) => {
        const tone = m.tone === "success" ? "text-success" : m.tone === "warning" ? "text-warning" : m.tone === "danger" ? "text-danger" : "text-text-primary";
        return (
          <div key={i} className="rounded-xl bg-bg-raised p-3">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">{m.label}</p>
            <p className={`text-lg font-semibold mt-1 ${tone}`}>{m.value}</p>
          </div>
        );
      })}
    </div>
  );
}

export function ProgressBar({ value, color = "var(--success, #00d97e)" }: { value: number; color?: string }): React.ReactElement {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  const tone = pct >= 95 ? "bg-success" : pct >= 70 ? "bg-warning" : "bg-danger";
  return (
    <div className="h-2 rounded-full bg-bg-raised overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
      <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function SeverityPill({ severity }: { severity: "info" | "low" | "med" | "high" | "critical" }): React.ReactElement {
  const map: Record<typeof severity, [string, string]> = {
    info:     ["bg-bg-raised text-text-secondary", "提醒"],
    low:      ["bg-success/15 text-success",       "低"],
    med:      ["bg-warning/15 text-warning",       "中"],
    high:     ["bg-danger/10 text-danger",         "高"],
    critical: ["bg-danger/25 text-danger",         "緊急"],
  };
  const [cls, label] = map[severity];
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
}

export function StatusDot({ ok, label }: { ok: boolean; label: string }): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-muted">
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-success" : "bg-danger"}`} aria-hidden />
      {label}
    </span>
  );
}

export function PrimaryButton({ children, onClick, disabled, href }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean; href?: string }): React.ReactElement {
  const className = "block w-full text-center h-12 rounded-xl bg-accent text-text-inverse font-semibold text-sm hover:bg-accent-hover active:bg-accent-pressed disabled:bg-bg-raised disabled:text-text-muted disabled:cursor-not-allowed transition-colors";
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>;
  return <button onClick={onClick} disabled={disabled} className={className}>{children}</button>;
}

export function SecondaryButton({ children, onClick, href }: { children: React.ReactNode; onClick?: () => void; href?: string }): React.ReactElement {
  const className = "block w-full text-center h-11 rounded-xl bg-bg-raised text-text-primary font-medium text-sm hover:bg-bg-hover transition-colors";
  if (href) return <a href={href} className={className}>{children}</a>;
  return <button onClick={onClick} className={className}>{children}</button>;
}

export function EmptyMobile({ title, body }: { title: string; body?: string }): React.ReactElement {
  return (
    <Card className="text-center">
      <p className="text-sm text-text-secondary">{title}</p>
      {body && <p className="text-xs text-text-muted mt-2">{body}</p>}
    </Card>
  );
}

/** Helper: spinner used while page-level data loads. */
export function MobileSpinner({ message = "讀取中…" }: { message?: string }): React.ReactElement {
  return (
    <div className="min-h-[100dvh] bg-bg-base text-text-primary flex items-center justify-center p-6">
      <div className="flex items-center gap-3">
        <span className="w-2 h-2 rounded-full bg-accent animate-pulse" aria-hidden />
        <span className="text-xs uppercase tracking-wider text-text-muted">{message}</span>
      </div>
    </div>
  );
}

/**
 * Big tap-target list row used on landing pages — pairs an icon, a primary
 * label, an optional sublabel, and an optional right-side accessory (count,
 * badge). Always renders a 56px-min row so it clears the iOS HIG touch
 * target. `onClick` is required for actionability; pages should use this
 * for nav, never for hover-state cards.
 */
export function ListRow({
  label, sublabel, accessory, onClick, disabled = false,
}: {
  label: string;
  sublabel?: string;
  accessory?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left min-h-[56px] py-3 px-3 rounded-xl bg-bg-raised hover:bg-bg-hover active:bg-bg-pressed disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-3"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate text-text-primary">{label}</p>
        {sublabel && <p className="text-[11px] text-text-muted mt-0.5 truncate">{sublabel}</p>}
      </div>
      {accessory ?? <span className="text-text-muted text-xs">→</span>}
    </button>
  );
}

/**
 * Polished "即將推出" card used on LIFF pages whose backing API isn't ready.
 * Renders a labelled banner + a bullet list of what the page WILL show.
 * This is intentionally distinct from EmptyMobile (which is for "no data
 * right now") and from the dashboard's <ComingSoon> (which assumes a desk
 * viewport). On phones, padding matters: keep the card tight and the bullet
 * list scannable.
 */
export function ComingSoonCard({
  title,
  description,
  willShow,
  ctaLabel,
  ctaHref,
}: {
  title: string;
  description: string;
  willShow?: string[];
  ctaLabel?: string;
  ctaHref?: string;
}): React.ReactElement {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block px-1.5 py-0.5 rounded bg-accent/15 text-accent text-[10px] font-semibold uppercase tracking-wider">即將推出</span>
        <span className="text-[10px] uppercase tracking-wider text-text-muted">Phase 4+</span>
      </div>
      <h2 className="text-base font-semibold mb-1">{title}</h2>
      <p className="text-sm text-text-secondary leading-relaxed mb-3">{description}</p>
      {willShow && willShow.length > 0 && (
        <div className="rounded-xl bg-bg-raised p-3 mb-3">
          <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2">完成後將提供</p>
          <ul className="space-y-1.5">
            {willShow.map((line, i) => (
              <li key={i} className="text-xs text-text-secondary flex gap-2">
                <span className="text-accent shrink-0 mt-[3px]">●</span>
                <span className="min-w-0">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {ctaLabel && ctaHref && (
        <PrimaryButton href={ctaHref}>{ctaLabel}</PrimaryButton>
      )}
    </Card>
  );
}

/**
 * Greeting strip used at the top of "me"-type pages — shows the user's
 * picture, display name, and role chip. Reads everything from useLiff().
 */
export function ProfileStrip({
  profile,
  roleLabel,
  tenantId,
}: {
  profile: { displayName: string; pictureUrl?: string };
  roleLabel: string;
  tenantId?: string;
}): React.ReactElement {
  return (
    <Card className="!p-3">
      <div className="flex items-center gap-3">
        {profile.pictureUrl ? (
          <img src={profile.pictureUrl} alt="" className="w-12 h-12 rounded-full bg-bg-raised" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-bg-raised flex items-center justify-center text-text-muted text-lg">
            {profile.displayName.slice(0, 1)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{profile.displayName}</p>
          <p className="text-[11px] text-text-muted truncate">
            {roleLabel}
            {tenantId && <span> · {tenantId.slice(0, 8)}…</span>}
          </p>
        </div>
      </div>
    </Card>
  );
}
